"use client";

import type React from "react";
import { useState } from "react";
import {
  FormControl,
  FormLabel,
  Text,
  Switch,
  HStack,
  Box,
  Link,
  IconButton,
  Tooltip,
} from "@chakra-ui/react";
import { ExternalLinkIcon, InfoIcon } from "@chakra-ui/icons";
import { useAppTheme } from "../../../ThemeContext.tsx";

interface SpeechNormalizationToggleProps {
  value: boolean | undefined;
  onChange: (value: boolean) => Promise<boolean> | void;
}

export const SpeechNormalizationToggle: React.FC<
  SpeechNormalizationToggleProps
> = ({ value = false, onChange }) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsUpdating(true);
    try {
      await onChange(e.target.checked);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <FormControl>
      <HStack justify="space-between" align="flex-start">
        <Box>
          <HStack align="center" mb={1}>
            <FormLabel
              fontSize="sm"
              fontWeight="medium"
              color={isDark ? "gray.300" : "gray.700"}
              mb={0}
            >
              Enable Speech Normalization
            </FormLabel>
            <Tooltip
              label="Converts text elements to their spoken form for better speech synthesis"
              placement="top"
            >
              <IconButton
                aria-label="Info"
                icon={<InfoIcon />}
                size="xs"
                variant="ghost"
                color={isDark ? "gray.400" : "gray.600"}
                marginLeft={-5}
              />
            </Tooltip>
          </HStack>
          <Text fontSize="xs" color={isDark ? "gray.400" : "gray.600"}>
            It converts text elements like numbers, currency, and dates into
            human-like spoken forms.
            {/* (
            <Link
              href="https://docs.retellai.com/build/speech-controllability"
              color={isDark ? "gray.300" : "gray.600"}
              isExternal
            >
              Learn more <ExternalLinkIcon mx="2px" />
            </Link>
            ) */}
          </Text>
        </Box>
        <Switch
          isChecked={value}
          onChange={handleChange}
          isDisabled={isUpdating}
          colorScheme={isDark ? "whiteAlpha" : "blackAlpha"}
          borderColor={isDark ? "gray.500" : "gray.400"}
          sx={{
            ".chakra-switch__track": {
              backgroundColor: value
                ? isDark
                  ? "gray.600"
                  : "gray.400" // Darker color when turned on
                : isDark
                ? "gray.700"
                : "gray.300", // Color when turned off
            },
          }}
        />
      </HStack>
    </FormControl>
  );
};
